import type { ICreateStockRecipeDto } from '@/api/services/stock-recipes.service';
import { IStockItem } from '@/interfaces/IStockItem';
import { IStockRecipe } from '@/interfaces/IStockRecipe';

/**
 * «Stock» of the product editor (plan §5.12): the supplies each unit (or
 * each size) uses in the current branch, edited in place and saved with the
 * product through the existing mass update (the flow of
 * `ModalsItemStock.vue`).
 */
export interface IStockRow {
  key: string;
  /** null = new (not saved yet). */
  id: number | null;
  stockItemId: number;
  stockItem: IStockItem | null;
  /** The size it applies to; null = every unit of the product. */
  optionId: number | null;
  optionItemId: number | null;
  quantityText: string;
}

export interface IStockMassUpdate {
  toAdd: ICreateStockRecipeDto[];
  toUpdate: { id: number; quantity: number }[];
  toDelete: number[];
}

let keySequence = 0;
function nextKey(): string {
  keySequence += 1;
  return `k${keySequence}`;
}

/** «0,25», «1.5», «2» → a positive quantity (3 decimals); null otherwise. */
export function parseQuantityInput(text: string): number | null {
  const normalized = (text ?? '').trim().replace(/\s/g, '').replace(',', '.');
  if (!/^\d+(\.\d+)?$/.test(normalized)) return null;
  const value = Math.round(Number(normalized) * 1000) / 1000;
  return Number.isFinite(value) && value > 0 ? value : null;
}

function quantityText(value: number): string {
  return String(Math.round(Number(value) * 1000) / 1000);
}

export function stockRowsFromRecipes(
  recipes: readonly IStockRecipe[]
): IStockRow[] {
  return recipes
    .filter((recipe) => recipe.itemId !== null)
    .map((recipe) => ({
      key: `r${recipe.id}`,
      id: recipe.id,
      stockItemId: recipe.stockItemId,
      stockItem: recipe.stockItem ?? null,
      optionId: recipe.optionId ?? null,
      optionItemId: recipe.optionItemId ?? null,
      quantityText: quantityText(recipe.quantity),
    }));
}

export function newStockRow(
  stockItem: IStockItem,
  size: { optionId: number; optionItemId: number } | null
): IStockRow {
  return {
    key: nextKey(),
    id: null,
    stockItemId: stockItem.id,
    stockItem,
    optionId: size?.optionId ?? null,
    optionItemId: size?.optionItemId ?? null,
    quantityText: '1',
  };
}

/** The supply is already counted for that size (or for every unit). */
export function hasStockRow(
  rows: readonly IStockRow[],
  stockItemId: number,
  optionItemId: number | null
): boolean {
  return rows.some(
    (row) =>
      row.stockItemId === stockItemId && row.optionItemId === optionItemId
  );
}

/** Keys of the rows whose quantity is not a positive number. */
export function invalidStockRows(rows: readonly IStockRow[]): string[] {
  return rows
    .filter((row) => parseQuantityInput(row.quantityText) === null)
    .map((row) => row.key);
}

/** Something to save: rows added or removed, or a quantity retyped. */
export function stockRowsChanged(
  base: readonly IStockRow[],
  rows: readonly IStockRow[]
): boolean {
  if (base.length !== rows.length) return true;
  const baseByKey = new Map(base.map((row) => [row.key, row]));
  return rows.some((row) => {
    const stored = baseByKey.get(row.key);
    if (!stored) return true;
    const quantity = parseQuantityInput(row.quantityText);
    return quantity === null
      ? row.quantityText.trim() !== stored.quantityText.trim()
      : quantity !== parseQuantityInput(stored.quantityText);
  });
}

/**
 * The same saved lines: each line (by its id) with the same stock item,
 * size, quantity and unit. Names are not compared: a renamed stock item
 * saves the same; a new unit changes what the quantities mean.
 */
export function sameStockLines(
  a: readonly IStockRow[],
  b: readonly IStockRow[]
): boolean {
  if (a.length !== b.length) return false;
  const byId = new Map(b.map((row) => [row.id, row]));
  return a.every((row) => {
    const other = byId.get(row.id);
    return (
      row.id !== null &&
      other !== undefined &&
      other.stockItemId === row.stockItemId &&
      other.optionItemId === row.optionItemId &&
      parseQuantityInput(other.quantityText) ===
        parseQuantityInput(row.quantityText) &&
      (other.stockItem?.unit ?? null) === (row.stockItem?.unit ?? null)
    );
  });
}

/** The mass update from what was loaded to what is edited now. */
export function stockMassUpdate(
  base: readonly IStockRow[],
  rows: readonly IStockRow[],
  context: { restaurantId: number; branchId: number; itemId: number }
): IStockMassUpdate {
  const baseById = new Map(
    base.flatMap((row) => (row.id !== null ? [[row.id, row] as const] : []))
  );
  const keptIds = new Set(
    rows.flatMap((row) => (row.id !== null ? [row.id] : []))
  );
  const toAdd: ICreateStockRecipeDto[] = [];
  const toUpdate: { id: number; quantity: number }[] = [];
  for (const row of rows) {
    const quantity = parseQuantityInput(row.quantityText);
    if (quantity === null) continue;
    if (row.id === null) {
      toAdd.push({
        restaurantId: context.restaurantId,
        branchId: context.branchId,
        stockItemId: row.stockItemId,
        itemId: context.itemId,
        ...(row.optionId !== null ? { optionId: row.optionId } : {}),
        ...(row.optionItemId !== null
          ? { optionItemId: row.optionItemId }
          : {}),
        quantity,
      });
      continue;
    }
    const stored = baseById.get(row.id);
    if (stored && parseQuantityInput(stored.quantityText) !== quantity)
      toUpdate.push({ id: row.id, quantity });
  }
  const toDelete = [...baseById.keys()].filter((id) => !keptIds.has(id));
  return { toAdd, toUpdate, toDelete };
}

export function isEmptyStockUpdate(update: IStockMassUpdate): boolean {
  return (
    !update.toAdd.length && !update.toUpdate.length && !update.toDelete.length
  );
}
